import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Plus, Edit, User, Users, ShieldCheck } from 'lucide-react';
import CustomerAvatar from '../../../../components/common/CustomerAvatar';
import CustomerAccessTab from './CustomerAccessTab';
import { useAuth } from '../../../../context/AuthContext';
import { getCustomerCategories, createCustomerCategory, updateCustomerCategory, getSalesPersons, getCountries, getStatesByCountry } from '../../../../services/api/lookupApi';
import { normalizeRole } from '../../../../config/roles';
import { handleApiError } from '../../../../services/api/customerApi';

const CustomerEditModal = ({ customer, role, onClose, onSave }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('general'); // 'general' | 'assignments' | 'access'
    const [saveError, setSaveError] = useState(null);
    const isOwnerOrAdmin = user?.roles?.some(r => ['TENANT_OWNER', 'TENANT_ADMIN', 'SYSTEM_ADMIN'].includes(normalizeRole(r)));
    const canManageAccess = ['TENANT_OWNER', 'TENANT_ADMIN'].includes(role);

    const [formData, setFormData] = useState({
        id: customer?.id || customer?.publicId || null,
        customerId: customer?.customerId || '',
        name: customer?.name || '',
        categoryId: customer?.categoryId || '',
        whatsappNumber: customer?.whatsappNumber || '',
        contactPerson: customer?.contactPerson || '',
        designation: customer?.designation || '',
        mobileNumber: customer?.mobileNumber || '',
        email: customer?.email || '',
        trn: customer?.trn || '',
        address: customer?.address || '',
        countryId: customer?.countryId || '',
        stateId: customer?.stateId || '',
        assignedSalesPersonPublicIds: customer?.assignedSalesPersons?.map(sp => sp.publicId) || [],
        delegatedSalesPersonPublicIds: customer?.delegatedSalesPersons?.map(sp => sp.publicId) || [],
        priceType: customer?.priceType || 'RETAIL',
        image: customer?.image || null
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Lookup data
    const [categories, setCategories] = useState([]);
    const [salesPersons, setSalesPersons] = useState([]);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [loadingLookups, setLoadingLookups] = useState(true);

    const [creatingCategory, setCreatingCategory] = useState(false);
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [showEditCategory, setShowEditCategory] = useState(false);
    const [editCategoryName, setEditCategoryName] = useState('');
    const [updatingCategory, setUpdatingCategory] = useState(false);

    // Load lookup data
    useEffect(() => {
        const loadLookups = async () => {
            try {
                setLoadingLookups(true);
                const [categoriesData, salesPersonsData, countriesData] = await Promise.all([
                    getCustomerCategories(),
                    getSalesPersons(),
                    getCountries()
                ]);
                setCategories(categoriesData);
                setSalesPersons(salesPersonsData);
                setCountries(countriesData);

                // Load states if country is already selected
                if (customer?.countryId) {
                    const statesData = await getStatesByCountry(customer.countryId);
                    setStates(statesData);
                }
            } catch (error) {
                console.error('Error loading lookups:', error);
            } finally {
                setLoadingLookups(false);
            }
        };

        loadLookups();
    }, [customer?.countryId]);

    // Load states when country changes
    const handleCountryChange = async (countryId) => {
        setFormData(prev => ({ ...prev, countryId, stateId: '' }));

        if (countryId) {
            try {
                const statesData = await getStatesByCountry(countryId);
                setStates(statesData);
            } catch (error) {
                console.error('Error loading states:', error);
                setStates([]);
            }
        } else {
            setStates([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSalesPersonToggle = (salesPersonId, role) => {
        const key = role === 'assigned' ? 'assignedSalesPersonPublicIds' : 'delegatedSalesPersonPublicIds';
        setFormData(prev => {
            const ids = prev[key] || [];
            if (ids.includes(salesPersonId)) {
                return { ...prev, [key]: ids.filter(id => id !== salesPersonId) };
            } else {
                return { ...prev, [key]: [...ids, salesPersonId] };
            }
        });
    };

    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) return;

        setCreatingCategory(true);
        try {
            const response = await createCustomerCategory(newCategoryName);
            // Verify structure of response - controller returns map with publicId
            const newCategory = { publicId: response.publicId, name: newCategoryName };

            setCategories(prev => [...prev, newCategory]);
            setFormData(prev => ({ ...prev, categoryId: newCategory.publicId }));
            setNewCategoryName('');
            setShowAddCategory(false);
        } catch (error) {
            console.error('Error creating category:', error);
            alert('Failed to create category');
        } finally {
            setCreatingCategory(false);
        }
    };

    const handleUpdateCategory = async () => {
        if (!editCategoryName.trim() || !formData.categoryId) return;

        setUpdatingCategory(true);
        try {
            await updateCustomerCategory(formData.categoryId, editCategoryName);
            
            // Update local state
            setCategories(prev => prev.map(cat => 
                cat.publicId === formData.categoryId ? { ...cat, name: editCategoryName } : cat
            ));
            
            setShowEditCategory(false);
            setEditCategoryName('');
        } catch (error) {
            console.error('Error updating category:', error);
            alert('Failed to update category');
        } finally {
            setUpdatingCategory(false);
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Customer name is required';
        }

        if (!formData.categoryId) {
            newErrors.categoryId = 'Category is required';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setLoading(true);
        setSaveError(null);
        try {
            await onSave(formData);
            onClose();
        } catch (error) {
            console.error('Error saving customer:', error);
            setSaveError(handleApiError(error));
        } finally {
            setLoading(false);
        }
    };

    if (loadingLookups) {
        return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                    <Loader2 size={40} className="animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 font-semibold text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] md:max-h-[85vh] flex flex-col overflow-hidden">
                {/* Compact component to fit in one view if possible, using max-w-5xl for width */}

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <CustomerAvatar name={formData.name} image={formData.image} size="md" />
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">
                                {(customer?.id || customer?.publicId) ? 'Edit Customer' : 'Add New Customer'}
                            </h2>
                            <p className="text-xs text-gray-500">
                                {formData.customerId ? `ID: ${formData.customerId}` : 'Enter customer details below'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                        disabled={loading}
                    >
                        <X size={20} />
                    </button>
                </div>

                {saveError && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2.5 text-red-700 text-xs font-semibold animate-in fade-in duration-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 animate-ping" />
                        <span>{saveError}</span>
                    </div>
                )}

                {/* Tabs */}
                <div className="px-6 border-b border-gray-100 flex items-center gap-6">
                    <button
                        type="button"
                        onClick={() => setActiveTab('general')}
                        className={`py-3 px-1 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${activeTab === 'general' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    >
                        <User size={16} />
                        General Info
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('assignments')}
                        className={`py-3 px-1 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${activeTab === 'assignments' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    >
                        <Users size={16} />
                        Assignments
                    </button>
                    {canManageAccess && customer?.id && (
                        <button
                            type="button"
                            onClick={() => setActiveTab('access')}
                            className={`py-3 px-1 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${activeTab === 'access' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                        >
                            <ShieldCheck size={16} />
                            Portal Access
                        </button>
                    )}
                </div>

                {/* Content Area */}
                <div className="flex-1 min-h-0 flex flex-col bg-white">
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        {activeTab === 'general' && (
                            <div className="grid grid-cols-12 gap-6 animate-in fade-in slide-in-from-left-2 duration-300">
                                {/* Left Column: Essential Info & Contact */}
                                <div className="col-span-12 lg:col-span-7 space-y-5">
                            {/* Basic Info Group */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                    Basic Information
                                </h3>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Customer Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all ${errors.name ? 'border-red-500' : 'border-gray-200'}`}
                                            placeholder="e.g. Al Futtaim Group"
                                        />
                                        {errors.name && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Category <span className="text-red-500">*</span>
                                        </label>
                                        {showAddCategory ? (
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={newCategoryName}
                                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                                    placeholder="New Category Name"
                                                    className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleAddCategory}
                                                    disabled={creatingCategory || !newCategoryName.trim()}
                                                    className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 whitespace-nowrap"
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddCategory(false)}
                                                    className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : showEditCategory ? (
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={editCategoryName}
                                                    onChange={(e) => setEditCategoryName(e.target.value)}
                                                    placeholder="Edit Category Name"
                                                    className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleUpdateCategory}
                                                    disabled={updatingCategory || !editCategoryName.trim()}
                                                    className="px-3 py-1 bg-orange-600 text-white rounded-lg text-xs font-bold hover:bg-orange-700 whitespace-nowrap"
                                                >
                                                    Update
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowEditCategory(false)}
                                                    className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <select
                                                    name="categoryId"
                                                    value={formData.categoryId}
                                                    onChange={handleChange}
                                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white ${errors.categoryId ? 'border-red-500' : 'border-gray-200'}`}
                                                >
                                                    <option value="">Select category</option>
                                                    {categories.map(cat => (
                                                        <option key={cat.publicId} value={cat.publicId}>
                                                            {cat.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddCategory(true)}
                                                    className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 border border-blue-100 transition-colors"
                                                    title="Add New Category"
                                                >
                                                    <Plus size={16} />
                                                </button>
                                                {formData.categoryId && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentCat = categories.find(c => c.publicId === formData.categoryId);
                                                            if (currentCat) {
                                                                setEditCategoryName(currentCat.name);
                                                                setShowEditCategory(true);
                                                            }
                                                        }}
                                                        className="p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 border border-orange-100 transition-colors"
                                                        title="Edit Selected Category"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {errors.categoryId && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.categoryId}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Customer ID <span className="text-gray-400 font-normal">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="customerId"
                                            value={formData.customerId}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            placeholder="External Ref ID"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            TRN <span className="text-gray-400 font-normal">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="trn"
                                            value={formData.trn}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            placeholder="Tax Registration No."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Price Type
                                        </label>
                                        <select
                                            name="priceType"
                                            value={formData.priceType}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                        >
                                            <option value="RETAIL">Retail</option>
                                            <option value="WHOLESALE">Wholesale</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Info Group */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                    Contact Details
                                </h3>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Contact Person
                                        </label>
                                        <input
                                            type="text"
                                            name="contactPerson"
                                            value={formData.contactPerson}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            placeholder="Full Name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Designation
                                        </label>
                                        <input
                                            type="text"
                                            name="designation"
                                            value={formData.designation}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            placeholder="Position"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Mobile Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="mobileNumber"
                                            value={formData.mobileNumber}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            placeholder="+971 50..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            WhatsApp
                                        </label>
                                        <input
                                            type="tel"
                                            name="whatsappNumber"
                                            value={formData.whatsappNumber}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            placeholder="+971 50..."
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                                            placeholder="name@company.com"
                                        />
                                        {errors.email && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.email}</p>}
                                    </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Address/Location */}
                            <div className="col-span-12 lg:col-span-5 space-y-5">
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                        Location
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                                Address Line
                                            </label>
                                            <input
                                                type="text"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                placeholder="Building, Street, Area"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                                    Country
                                                </label>
                                                <select
                                                    name="countryId"
                                                    value={formData.countryId}
                                                    onChange={(e) => handleCountryChange(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                                >
                                                    <option value="">Select...</option>
                                                    {countries.map(country => (
                                                        <option key={country.publicId} value={country.publicId}>
                                                            {country.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                                    State/Emirate
                                                </label>
                                                <select
                                                    name="stateId"
                                                    value={formData.stateId}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white disabled:bg-gray-100"
                                                    disabled={!formData.countryId}
                                                >
                                                    <option value="">Select...</option>
                                                    {states.map(state => (
                                                        <option key={state.publicId} value={state.publicId}>
                                                            {state.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                        {activeTab === 'assignments' && (
                            <div className="grid grid-cols-1 gap-6 animate-in fade-in slide-in-from-right-2 duration-300">
                                {/* Assigned Sales Team */}
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                                    Assigned Sales Team
                                </h3>

                                <div className="max-h-44 overflow-y-auto pr-1 space-y-2">
                                    {salesPersons.map(sp => {
                                        const isAssigned = (formData.assignedSalesPersonPublicIds || []).includes(sp.publicId);
                                        return (
                                            <label
                                                key={sp.publicId}
                                                className={`flex items-center justify-between gap-3 cursor-pointer p-2.5 rounded-lg transition-all border ${isAssigned
                                                    ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-100'
                                                    : 'bg-white border-gray-200 hover:border-gray-300'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={isAssigned}
                                                        onChange={() => handleSalesPersonToggle(sp.publicId, 'assigned')}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                                    />
                                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isAssigned ? 'bg-blue-500 text-white' : 'bg-orange-100 text-orange-600'}`}>
                                                        {sp.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className={`text-sm font-medium ${isAssigned ? 'text-blue-800' : 'text-gray-700'}`}>{sp.name}</span>
                                                </div>
                                                {isAssigned && <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full uppercase">Assigned</span>}
                                            </label>
                                        );
                                    })}
                                    {salesPersons.length === 0 && <p className="text-xs text-gray-500 italic text-center py-4">No sales persons available</p>}
                                </div>
                            </div>

                            {/* Delegated Sales Team */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                    Delegated Sales Team
                                </h3>

                                <div className="max-h-44 overflow-y-auto pr-1 space-y-2">
                                    {salesPersons.map(sp => {
                                        const isDelegated = (formData.delegatedSalesPersonPublicIds || []).includes(sp.publicId);
                                        return (
                                            <label
                                                key={sp.publicId}
                                                className={`flex items-center justify-between gap-3 cursor-pointer p-2.5 rounded-lg transition-all border ${isDelegated
                                                    ? 'bg-purple-50 border-purple-300 ring-1 ring-purple-100'
                                                    : 'bg-white border-gray-200 hover:border-gray-300'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={isDelegated}
                                                        onChange={() => handleSalesPersonToggle(sp.publicId, 'delegated')}
                                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                                                    />
                                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isDelegated ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-600'}`}>
                                                        {sp.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className={`text-sm font-medium ${isDelegated ? 'text-purple-800' : 'text-gray-700'}`}>{sp.name}</span>
                                                </div>
                                                {isDelegated && <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full uppercase">Delegated</span>}
                                            </label>
                                        );
                                    })}
                                    {salesPersons.length === 0 && <p className="text-xs text-gray-500 italic text-center py-4">No sales persons available</p>}
                                </div>
                            </div>
                        </div>
                    )}

                        {activeTab === 'access' && (
                            <div className="animate-in fade-in zoom-in-95 duration-300">
                                <CustomerAccessTab 
                                    customerId={customer.id} 
                                    customerCountryId={formData.countryId}
                                    countries={countries}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer Actions - Only show Save for non-access tabs */}
                    <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-white flex-shrink-0 z-10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm"
                            disabled={loading}
                        >
                            {activeTab === 'access' ? 'Close' : 'Cancel'}
                        </button>
                        
                        {activeTab !== 'access' && (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all font-semibold text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Save Customer
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerEditModal;
